import { useContext, useEffect, useState } from "react";
import api from "../api/client";
import AuthContext from "../context/AuthContext";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useScreen } from "../hooks/useScreen";

const emptyForm = {
  name: "",
  lastname: "",
  email: "",
  password: "",
  authorized: true,
  is_admin: false,
};

export default function Users() {
  const { user } = useContext(AuthContext);
  const { isXs } = useScreen();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const showError = (message) => {
    setError(message);
  };

  const isAdmin = Boolean(user?.is_admin);

  const columns = [
    {
      field: "fullName",
      headerName: "Nombre",
      flex: 1.2,
      valueGetter: (_, row) => `${row.name} ${row.lastname}`,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.4,
    },
    {
      field: "authorized",
      headerName: "Autorizado",
      flex: 0.8,
      valueGetter: (_, row) => (row.authorized ? "Sí" : "No"),
    },
    {
      field: "is_admin",
      headerName: "Admin",
      flex: 0.7,
      valueGetter: (_, row) => (row.is_admin ? "Sí" : "No"),
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 1.2,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1, py: 1 }}>
          <Button size="small" color="warning" onClick={() => handleOpenEdit(params.row)}>
            Editar
          </Button>
          <Button
            size="small"
            color="error"
            disabled={params.row.id === user?.id}
            onClick={() => handleDeleteUser(params.row.id)}
          >
            Eliminar
          </Button>
        </Box>
      ),
    },
  ];

  const fetchUsers = () => {
    if (!isAdmin) return;

    api
      .get("/users/")
      .then((res) => {
        setUsers(res.data);
        setError("");
      })
      .catch((err) => {
        showError(err?.response?.data?.detail || "No se pudieron cargar los usuarios");
      });
  };

  useEffect(() => {
    fetchUsers();
  }, [isAdmin]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleOpenCreate = () => {
    setEditing(false);
    setFormData(emptyForm);
    setOpen(true);
  };

  const handleOpenEdit = (listedUser) => {
    setEditing(true);
    setSelectedUser(listedUser);
    setFormData({
      name: listedUser.name,
      lastname: listedUser.lastname,
      email: listedUser.email,
      password: "",
      authorized: listedUser.authorized,
      is_admin: listedUser.is_admin,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(false);
    setSelectedUser(null);
    setFormData(emptyForm);
  };

  const handleSaveUser = () => {
    const payload = {
      ...formData,
      client_id: user.client_id,
    };

    const request = editing
      ? api.put(`/users/${selectedUser.id}`, {
          name: formData.name,
          lastname: formData.lastname,
          email: formData.email,
          password: formData.password || undefined,
          authorized: formData.authorized,
          is_admin: formData.is_admin,
        })
      : api.post("/users/", payload);

    request
      .then(() => {
        setSuccessMessage(editing ? "Usuario actualizado correctamente" : "Usuario dado de alta correctamente");
        handleClose();
        fetchUsers();
      })
      .catch((err) => {
        showError(err?.response?.data?.detail || "No se pudo guardar el usuario");
      });
  };

  const handleDeleteUser = (id) => {
    if (!window.confirm("¿Seguro que querés eliminar este usuario?")) return;

    api
      .delete(`/users/${id}`)
      .then(() => {
        setSuccessMessage("Usuario eliminado correctamente");
        fetchUsers();
      })
      .catch((err) => {
        showError(err?.response?.data?.detail || "No se pudo eliminar el usuario");
      });
  };

  if (!isAdmin) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Usuarios
        </Typography>
        <Alert severity="warning">Solo un administrador puede gestionar usuarios.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Usuarios
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" onClick={handleOpenCreate}>
          Agregar usuario
        </Button>
      </Box>

      <Box sx={{ width: "100%", height: isXs ? 420 : 520 }}>
        <DataGrid
          rows={users}
          columns={columns}
          disableColumnMenu={isXs}
          density={isXs ? "compact" : "standard"}
          initialState={{
            pagination: { paginationModel: { pageSize: isXs ? 5 : 10 } },
          }}
          pageSizeOptions={[5, 10]}
          disableRowSelectionOnClick
        />
      </Box>

      <Dialog open={open} onClose={handleClose} fullScreen={isXs} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Editar usuario" : "Agregar usuario"}</DialogTitle>
        <DialogContent>
          <TextField margin="dense" fullWidth label="Nombre" name="name" value={formData.name} onChange={handleChange} />
          <TextField margin="dense" fullWidth label="Apellido" name="lastname" value={formData.lastname} onChange={handleChange} />
          <TextField margin="dense" fullWidth label="Email" name="email" value={formData.email} onChange={handleChange} />
          <TextField
            margin="dense"
            fullWidth
            type="password"
            label={editing ? "Nueva password (opcional)" : "Password"}
            name="password"
            value={formData.password}
            onChange={handleChange}
          />
          <FormControlLabel
            control={<Checkbox name="authorized" checked={formData.authorized} onChange={handleChange} disabled={editing && selectedUser?.id === user?.id} />}
            label="Usuario autorizado"
          />
          <FormControlLabel
            control={<Checkbox name="is_admin" checked={formData.is_admin} onChange={handleChange} disabled={editing && selectedUser?.id === user?.id} />}
            label="Administrador"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveUser}>
            {editing ? "Actualizar" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSuccessMessage("")} severity="success" variant="filled" sx={{ width: "100%" }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={4000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setError("")} severity="error" variant="filled" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
